import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api, ORDER_STATUS_LABELS, PAYMENT_STATUS_LABELS, fmtINR, API_BASE } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Plus, Home, Pill, TestTube2, HeartPulse, CheckCircle2, XCircle, FileText, Search, Bell, Eye } from "lucide-react";
import { toast } from "sonner";
import PrescriptionReviewModal from "@/components/PrescriptionReviewModal";

const TYPE_LABELS = {
  full_body_checkup: "Full Body Checkup",
  medicine: "Medicine",
  lab_test: "Lab Test",
};

const TYPE_ICONS = {
  full_body_checkup: HeartPulse,
  medicine: Pill,
  lab_test: TestTube2,
};

const statusColor = (s) => ({
  pending: "bg-amber-100 text-amber-800",
  scheduled: "bg-blue-100 text-blue-800",
  sample_collected: "bg-indigo-100 text-indigo-800",
  report_ready: "bg-teal-100 text-teal-800",
  delivered: "bg-emerald-100 text-emerald-800",
  cancelled: "bg-red-100 text-red-800",
}[s] || "bg-gray-100 text-gray-800");

const payColor = (s) => ({
  pending: "bg-amber-100 text-amber-800",
  paid: "bg-emerald-100 text-emerald-800",
  failed: "bg-red-100 text-red-800",
  refunded: "bg-gray-100 text-gray-800",
}[s] || "");

const SLOTS = ["07:00-09:00", "09:00-11:00", "11:00-13:00", "16:00-18:00"];

const emptyForm = {
  order_type: "full_body_checkup",
  member_id: "",
  slot_date: "",
  slot_time: SLOTS[0],
  amount: 1499,
  items_text: "",
  tests_text: "",
  notes: "",
};

export default function Orders() {
  const [orders, setOrders] = useState([]);
  const [members, setMembers] = useState([]);
  const [typeTab, setTypeTab] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [fbcEligible, setFbcEligible] = useState(null); // null | {eligible, existing_order}
  const [rxOrder, setRxOrder] = useState(null); // order object for review modal
  const [rxOpen, setRxOpen] = useState(false);

  const load = useCallback(async () => {
    const params = {};
    if (statusFilter !== "all") params.status = statusFilter;
    if (typeTab !== "all") params.order_type = typeTab;
    if (q) params.q = q;
    const [o, m] = await Promise.all([
      api.get("/orders", { params }),
      api.get("/members"),
    ]);
    setOrders(o.data);
    setMembers(m.data);
  }, [statusFilter, typeTab, q]);

  useEffect(() => { load(); }, [load]);

  // Auto-refresh every 15s so new WA-bot orders appear without a manual
  // reload. Pauses when the browser tab is hidden to save bandwidth + rate.
  useEffect(() => {
    const tick = () => { if (!document.hidden) load(); };
    const iv = setInterval(tick, 15000);
    const onVis = () => { if (!document.hidden) load(); };
    document.addEventListener("visibilitychange", onVis);
    return () => { clearInterval(iv); document.removeEventListener("visibilitychange", onVis); };
  }, [load]);

  const selectedMember = useMemo(
    () => members.find((m) => m.id === form.member_id),
    [members, form.member_id]
  );

  // Check FBC eligibility whenever member or type changes
  useEffect(() => {
    if (!form.member_id || form.order_type !== "full_body_checkup") {
      setFbcEligible(null);
      return;
    }
    api.get(`/orders/fbc-eligibility/${form.member_id}`)
      .then((r) => setFbcEligible(r.data))
      .catch(() => setFbcEligible(null));
  }, [form.member_id, form.order_type]);

  const openNew = (preselectType) => {
    setForm({ ...emptyForm, order_type: preselectType || "full_body_checkup", amount: preselectType === "full_body_checkup" ? 1499 : 0 });
    setFbcEligible(null);
    setOpen(true);
  };

  const submit = async () => {
    if (!form.member_id) { toast.error("Select a member"); return; }
    const payload = {
      order_type: form.order_type,
      member_id: form.member_id,
      amount: parseFloat(form.amount) || 0,
      notes: form.notes || null,
    };
    if (form.order_type === "full_body_checkup" || form.order_type === "lab_test") {
      if (!form.slot_date) { toast.error("Pick a date"); return; }
      payload.slot_date = form.slot_date;
      payload.slot_time = form.slot_time;
    }
    if (form.order_type === "medicine") {
      const parsed = form.items_text.split(",").map((s) => {
        const m = s.trim().match(/^(.*?)(?:\s*[x×X]\s*(\d+))?$/);
        return m ? { name: (m[1] || s).trim(), qty: m[2] ? parseInt(m[2], 10) : 1 } : null;
      }).filter(Boolean);
      if (parsed.length === 0) { toast.error("Add at least one medicine"); return; }
      payload.items = parsed;
    }
    if (form.order_type === "lab_test") {
      const tests = form.tests_text.split(",").map((s) => s.trim()).filter(Boolean);
      if (tests.length === 0) { toast.error("Add at least one test"); return; }
      payload.tests = tests;
    }
    try {
      await api.post("/orders", payload);
      toast.success("Order created");
      setOpen(false);
      setForm(emptyForm);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const updateOrder = async (id, patch) => {
    try {
      await api.patch(`/orders/${id}`, patch);
      toast.success("Updated");
      load();
    } catch { toast.error("Update failed"); }
  };

  const notifyOrder = async (id) => {
    try {
      const r = await api.post(`/notify/order/${id}`, {});
      const sim = r.data?.wa_response?.simulated;
      toast.success(sim ? "Notification simulated (no WA creds)" : "WhatsApp status update sent");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Notification failed");
    }
  };

  const orderTypeSummary = (o) => {
    if (o.order_type === "medicine") {
      const total = (o.items || []).reduce((n, it) => n + (it.qty || 1), 0);
      return `${(o.items || []).length} item(s) · ${total} unit(s)`;
    }
    if (o.order_type === "lab_test") return (o.tests || []).join(", ") || o.package;
    return o.package || "Full Body Checkup";
  };

  const canSubmit = form.order_type !== "full_body_checkup" || (fbcEligible && fbcEligible.eligible !== false);

  return (
    <div className="p-8 space-y-6">
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="label-caps text-accent mb-2">Bookings</div>
          <h1 className="font-heading text-4xl tracking-tight text-primary">Orders</h1>
          <p className="text-muted-foreground mt-2 max-w-2xl">
            Process three streams: Full Body Checkup (once per member), Medicine orders, and Lab test bookings.
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button data-testid="new-fbc-order-btn" onClick={() => openNew("full_body_checkup")} variant="outline" className="rounded-full">
            <HeartPulse size={16} className="mr-1.5"/> Full body request
          </Button>
          <Button data-testid="new-medicine-order-btn" onClick={() => openNew("medicine")} variant="outline" className="rounded-full">
            <Pill size={16} className="mr-1.5"/> Medicine order
          </Button>
          <Button data-testid="new-lab-order-btn" onClick={() => openNew("lab_test")} className="bg-primary hover:bg-primary/90 rounded-full">
            <TestTube2 size={16} className="mr-1.5"/> Lab test order
          </Button>
        </div>
      </header>

      <Tabs value={typeTab} onValueChange={setTypeTab}>
        <TabsList data-testid="order-type-tabs" className="bg-secondary h-auto p-1">
          <TabsTrigger value="all" data-testid="tab-type-all">All types</TabsTrigger>
          <TabsTrigger value="full_body_checkup" data-testid="tab-type-fbc">
            <HeartPulse size={14} className="mr-1"/> Full Body Checkup
          </TabsTrigger>
          <TabsTrigger value="medicine" data-testid="tab-type-medicine">
            <Pill size={14} className="mr-1"/> Medicine
          </TabsTrigger>
          <TabsTrigger value="lab_test" data-testid="tab-type-labtest">
            <TestTube2 size={14} className="mr-1"/> Lab test
          </TabsTrigger>
        </TabsList>
      </Tabs>

      <Card className="p-4 border-border">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={16} />
          <Input data-testid="order-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by mobile number, member name, PIN or order number…" className="pl-10 h-11 rounded-lg" />
        </div>
      </Card>

      <div className="flex items-center gap-2 flex-wrap">
        <span className="label-caps text-muted-foreground mr-2">Status</span>
        {["all", ...Object.keys(ORDER_STATUS_LABELS)].map((s) => (
          <button
            key={s}
            data-testid={`filter-${s}`}
            onClick={() => setStatusFilter(s)}
            className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${statusFilter === s ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-secondary"}`}
          >
            {s === "all" ? "All" : ORDER_STATUS_LABELS[s]}
          </button>
        ))}
      </div>

      <Card className="border-border overflow-x-auto">
        <Table className="min-w-[1280px]">
          <TableHeader>
            <TableRow>
              <TableHead className="w-[110px]">Order</TableHead>
              <TableHead className="w-[130px]">Type</TableHead>
              <TableHead className="w-[160px]">Member</TableHead>
              <TableHead className="w-[220px]">Details</TableHead>
              <TableHead className="w-[140px]">Slot / PIN</TableHead>
              <TableHead className="w-[90px]">Amount</TableHead>
              <TableHead className="w-[170px]">Status</TableHead>
              <TableHead className="w-[130px]">Payment</TableHead>
              <TableHead className="w-[130px]">Rx</TableHead>
              <TableHead className="w-[150px]">Invoice</TableHead>
              <TableHead className="w-[100px] text-right">Notify</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {orders.length === 0 && <TableRow><TableCell colSpan={11} className="text-center py-10 text-muted-foreground">No orders in this view</TableCell></TableRow>}
            {orders.map((o) => {
              const Icon = TYPE_ICONS[o.order_type] || HeartPulse;
              return (
                <TableRow key={o.id} data-testid={`order-row-${o.id}`}>
                  <TableCell>
                    <div className="font-mono text-xs text-primary font-medium" data-testid={`order-code-${o.id}`}>{o.order_number || o.id}</div>
                    {o.home_collection && <span className="inline-flex items-center gap-1 text-xs text-primary mt-1"><Home size={12}/>Home</span>}
                  </TableCell>
                  <TableCell>
                    <span className="inline-flex items-center gap-1.5 text-sm">
                      <Icon size={14} className="text-primary"/> {TYPE_LABELS[o.order_type] || o.order_type}
                    </span>
                  </TableCell>
                  <TableCell>
                    <div className="font-medium">{o.member_name}</div>
                    <div className="text-xs text-muted-foreground">{o.phone}</div>
                  </TableCell>
                  <TableCell className="max-w-[220px]">
                    <div className="text-sm truncate" title={orderTypeSummary(o)}>{orderTypeSummary(o)}</div>
                  </TableCell>
                  <TableCell>
                    {o.slot_date ? (
                      <>
                        <div className="text-sm">{o.slot_date}</div>
                        <div className="text-xs text-muted-foreground">{o.slot_time} · {o.pin_code}</div>
                      </>
                    ) : (
                      <div className="text-xs font-mono text-muted-foreground">{o.pin_code}</div>
                    )}
                  </TableCell>
                  <TableCell className="font-medium">{fmtINR(o.amount)}</TableCell>
                  <TableCell>
                    <Select value={o.status} onValueChange={(v) => updateOrder(o.id, { status: v })}>
                      <SelectTrigger data-testid={`order-status-${o.id}`} className={`h-8 w-40 ${statusColor(o.status)} border-0 text-xs font-medium`}>
                        <SelectValue/>
                      </SelectTrigger>
                      <SelectContent>{Object.entries(ORDER_STATUS_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell>
                    <Select value={o.payment_status} onValueChange={(v) => updateOrder(o.id, { payment_status: v })}>
                      <SelectTrigger data-testid={`order-payment-${o.id}`} className={`h-8 w-32 ${payColor(o.payment_status)} border-0 text-xs font-medium`}>
                        <SelectValue/>
                      </SelectTrigger>
                      <SelectContent>{Object.entries(PAYMENT_STATUS_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell>
                    {(o.order_type === "medicine" || o.order_type === "lab_test") ? (
                      <Button
                        size="sm"
                        variant={o.rx_document_id ? "default" : (o.prescription_url ? "outline" : "ghost")}
                        onClick={() => { setRxOrder(o); setRxOpen(true); }}
                        data-testid={`rx-open-${o.id}`}
                        className="h-8 rounded-full text-xs"
                      >
                        <Eye size={12} className="mr-1" />
                        {o.rx_document_id ? "Review Rx" : (o.prescription_url ? "View Rx" : "Attach Rx")}
                      </Button>
                    ) : (
                      <span className="text-xs text-muted-foreground">—</span>
                    )}
                  </TableCell>
                  <TableCell>
                    {o.invoice_number ? (
                      <div className="flex items-center gap-1">
                        <a
                          href={`${API_BASE}/order-mgmt/orders/${o.id}/invoice/pdf`}
                          target="_blank"
                          rel="noreferrer"
                          data-testid={`order-invoice-${o.id}`}
                          className="inline-flex items-center gap-1 text-xs text-primary underline"
                        >
                          <FileText size={12}/> {o.invoice_number}
                        </a>
                        <Badge className={o.invoice_status === "sent" ? "bg-emerald-100 text-emerald-800 text-[10px]" : "bg-amber-100 text-amber-800 text-[10px]"}>
                          {o.invoice_status || "generated"}
                        </Badge>
                      </div>
                    ) : (
                      <span className="text-xs text-muted-foreground">Pending</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button size="sm" variant="outline" data-testid={`notify-order-${o.id}`} onClick={() => notifyOrder(o.id)} className="h-8 rounded-full">
                      <Bell size={14} className="mr-1"/> Notify
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Card>

      {/* Create Order Dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">New order</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label>Order type</Label>
              <Select value={form.order_type} onValueChange={(v) => setForm({ ...form, order_type: v, amount: v === "full_body_checkup" ? 1499 : 0 })}>
                <SelectTrigger data-testid="order-type-select"><SelectValue/></SelectTrigger>
                <SelectContent>
                  {Object.entries(TYPE_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Member</Label>
              <Select value={form.member_id} onValueChange={(v) => setForm({ ...form, member_id: v })}>
                <SelectTrigger data-testid="order-member-select"><SelectValue placeholder="Select a member"/></SelectTrigger>
                <SelectContent>
                  {members.map((m) => <SelectItem key={m.id} value={m.id}>{m.full_name} · {m.pin_code}</SelectItem>)}
                </SelectContent>
              </Select>
              {selectedMember && (
                <div className="text-xs text-muted-foreground mt-2">
                  {selectedMember.phone} · PIN {selectedMember.pin_code}{selectedMember.insurance_eligible && " · Insurance eligible"}
                </div>
              )}
            </div>

            {/* FBC eligibility indicator */}
            {form.order_type === "full_body_checkup" && form.member_id && fbcEligible && (
              <div
                data-testid="fbc-eligibility"
                className={`rounded-lg p-3 text-sm flex items-start gap-2 ${
                  fbcEligible.eligible ? "bg-emerald-50 text-emerald-800" : "bg-red-50 text-red-800"
                }`}
              >
                {fbcEligible.eligible ? <CheckCircle2 size={16} className="mt-0.5"/> : <XCircle size={16} className="mt-0.5"/>}
                <div>
                  {fbcEligible.eligible
                    ? "Eligible — this member has NOT yet availed the Full Body Checkup."
                    : `Already availed on ${fbcEligible.existing_order?.slot_date || "record"} (order ${fbcEligible.existing_order?.id}). Full Body Checkup can be booked only once per lifetime.`}
                </div>
              </div>
            )}

            {(form.order_type === "full_body_checkup" || form.order_type === "lab_test") && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Date</Label>
                  <Input data-testid="order-date-input" type="date" value={form.slot_date} onChange={(e) => setForm({ ...form, slot_date: e.target.value })} />
                </div>
                <div>
                  <Label>Slot</Label>
                  <Select value={form.slot_time} onValueChange={(v) => setForm({ ...form, slot_time: v })}>
                    <SelectTrigger><SelectValue/></SelectTrigger>
                    <SelectContent>{SLOTS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
            )}

            {form.order_type === "medicine" && (
              <div>
                <Label>Medicines (comma separated, use "x N" for quantity)</Label>
                <Textarea
                  data-testid="order-medicines-input"
                  rows={3}
                  placeholder="e.g. Paracetamol x2, Cetrizine x1, Vitamin D3 x1"
                  value={form.items_text}
                  onChange={(e) => setForm({ ...form, items_text: e.target.value })}
                />
              </div>
            )}

            {form.order_type === "lab_test" && (
              <div>
                <Label>Tests (comma separated)</Label>
                <Textarea
                  data-testid="order-tests-input"
                  rows={2}
                  placeholder="e.g. CBC, LFT, Vitamin B12"
                  value={form.tests_text}
                  onChange={(e) => setForm({ ...form, tests_text: e.target.value })}
                />
              </div>
            )}

            <div>
              <Label>Amount (₹)</Label>
              <Input data-testid="order-amount-input" type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} />
            </div>

            <div>
              <Label>Notes</Label>
              <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Optional" />
            </div>
          </div>
          <DialogFooter className="mt-4">
            <Button
              data-testid="save-order-btn"
              onClick={submit}
              disabled={!canSubmit}
              className="bg-primary hover:bg-primary/90 disabled:opacity-50"
            >
              Save order
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <PrescriptionReviewModal
        open={rxOpen}
        onOpenChange={(v) => { setRxOpen(v); if (!v) load(); }}
        order={rxOrder}
        onUpdated={() => load()}
      />
    </div>
  );
}
